// ─────────────────────────────────────────────────────────────────────────────
// Portfolio & Trading Rules guide page
//
// Two surfaces, one page — they belong together conceptually:
//   Portfolio = WHICH accounts you trade
//   Trading Rules = HOW each account trades
//
// Covers:
//   - adding/managing accounts
//   - the accounts list and what each field means
//   - per-account rules in three categories
//   - preset application for common prop firms
//   - the advisory-not-blocking nature of rules
//   - how rules surface elsewhere (Dashboard, Trade Entry, Analytics)
// ─────────────────────────────────────────────────────────────────────────────

import {
  GuideLayout, H2First, H2, H3, P, UL, LI, UI,
  ScreenshotFrame, Callout,
} from "../GuideLayout";

export function GuidePortfolio() {
  return (
    <GuideLayout
      title="Portfolio & ROI Tracker"
      subtitle="Manage your trading accounts, track prop firm ROI, and log payouts and expenses. Three tabs — Accounts, ROI Tracker, and Payouts & Expenses — all in one place."
      prev={{ slug: "eval-survival", label: "Eval Survival" }}
      next={{ slug: "strategies",    label: "Strategies" }}
    >
      <H2First>What it's for</H2First>
      <P>
        Futures OS is built around the idea that not all your trading
        accounts are the same. A TopStep combine has a $200 daily
        drawdown; an Apex funded account has different math; a sim
        account has no real rules at all. The Portfolio surface is
        where you tell the product which accounts you trade, track your
        real business ROI across prop firms, and log payouts and expenses.
      </P>
      <P>
        Three tabs handle three related jobs: <UI>Accounts</UI> is where
        you add and manage trading accounts. <UI>ROI Tracker</UI> shows
        your net profit across all prop firms — payouts minus expenses.
        <UI>Payouts & Expenses</UI> is where you log individual transactions.
      </P>

      <H2>Accounts tab — managing accounts</H2>
      <P>
        Open <UI>Portfolio</UI> from the sidebar and click the{" "}
        <UI>🏦 Accounts</UI> tab to see your full account list and the
        form for adding new ones.
      </P>

      <ScreenshotFrame
        src="/guide/portfolio-manage.png"
        alt="Manage Trading Accounts screen"
        caption="Add new accounts at the top; your existing accounts list below."
      />

      <H3>Adding an account</H3>
      <P>
        The <UI>Add New Account</UI> form takes six fields:
      </P>
      <UL>
        <LI><UI>Account Name</UI> — what you'll see this account called in dropdowns (e.g. "Apex $50K #1")</LI>
        <LI><UI>Prop Firm</UI> — select from the dropdown (Apex, TopStep, Bulenox, etc.) or choose Other to type a custom name</LI>
        <LI><UI>Account Type</UI> — Live Account, Prop Eval, Funded Account, or Sim. Prop Eval covers both evaluations and combines — firms use different names for the same account stage.</LI>
        <LI><UI>Platform</UI> — the trading platform you use (Rithmic, Tradovate, NinjaTrader, etc.) or Other to enter a custom one</LI>
        <LI><UI>Starting Balance</UI> — the account's funded balance</LI>
        <LI><UI>Payout Split (%)</UI> — your share of payouts (e.g. 80 means you keep 80%)</LI>
      </UL>
      <P>
        Click <UI>+ Add Account</UI> and the account appears in the
        list. From that moment it shows up in every account picker
        across the app. Each account can be edited after creation by
        clicking the <UI>Edit</UI> button on its row.
      </P>

      <H3>The accounts list</H3>
      <P>
        Below the form, your accounts are listed with name, account
        type and balance summary, and up to four buttons on the
        right: <UI>Edit</UI>, <UI>Passed</UI> (Prop Eval accounts
        only), <UI>Blown</UI>, and <UI>Delete</UI>.
      </P>

      <H3>Marking an eval or combine as passed</H3>
      <P>
        When a Prop Eval account passes, click <UI>Passed</UI> on
        that account's row rather than deleting it and adding a new
        account by hand. A modal opens with:
      </P>
      <UL>
        <LI><UI>Date Passed</UI> — defaults to today</LI>
        <LI>
          A <UI>New Funded Account</UI> section, pre-filled with the
          same prop firm and platform as the eval (those don't
          change), asking you to confirm the new starting balance,
          payout split, and compliance rules — funded terms are
          usually different from eval terms, so those aren't assumed
        </LI>
      </UL>
      <P>
        Click <UI>Confirm & Create Funded Account</UI> and two things
        happen: a new Funded account is created with the details you
        entered, and the eval account is flagged Passed rather than
        deleted. The eval's trade history and analytics stay exactly
        where they are — nothing is removed, and nothing moves over
        to the new account.
      </P>

      <Callout kind="tip" title="Why Passed instead of delete-and-recreate">
        The old approach — delete the eval, manually create a new
        funded account — throws away every trade you logged during
        the eval. Passed keeps that history intact under its own
        account (so your eval-stage analytics stay accurate) while
        giving the funded stage a clean slate to build its own
        record. The two accounts stay linked behind the scenes, so a
        combined view across both is possible later without merging
        any data by hand.
      </Callout>

      <H3>Marking an account as blown</H3>
      <P>
        When a prop firm account is breached — you hit max drawdown,
        violated a rule, or simply ran out of room — click{" "}
        <UI>Blown</UI> on that account's row. A modal opens asking for:
      </P>
      <UL>
        <LI><UI>Date Blown</UI> — defaults to today, change if the breach happened on a different day</LI>
        <LI><UI>Reason</UI> — optional but useful: "hit max drawdown", "revenge traded", "news event", etc.</LI>
      </UL>
      <P>
        Click <UI>Mark as Blown</UI> and the account is archived
        immediately. It disappears from the dashboard account selector
        and from all active dropdowns — but your full trade history,
        P&L, and ROI data are preserved exactly as they were.
      </P>

      <Callout kind="tip" title="Why Blown instead of Delete?">
        Blowing a prop account is part of the game — it happens to
        every prop trader. The data from a blown account is some of
        your most valuable: it shows exactly where your discipline
        broke down. Archiving keeps that record intact so Analytics
        and Eval Survival can still reference it. Delete wipes it
        permanently.
      </Callout>

      <H3>Archived accounts</H3>
      <P>
        Blown accounts appear in an <UI>💀 Blown / Archived Accounts</UI>{" "}
        section at the bottom of the Accounts tab. Each shows the
        account name with a red <UI>BLOWN</UI> badge, the date blown,
        the reason if you entered one, and a <UI>Restore</UI> button.
      </P>
      <P>
        Click <UI>Restore</UI> to move an account back to active — useful
        if you marked it blown by mistake, or if your prop firm reinstated
        it.
      </P>

      <P>
        Passed eval/combine accounts appear separately, in a{" "}
        <UI>🎓 Passed Evals / Combines</UI> section. Each row shows a
        green <UI>PASSED</UI> badge, the date passed, and an arrow
        pointing to the Funded account it converted into (e.g.{" "}
        <UI>→ Apex $50K Funded</UI>) so you can always trace which
        funded account a given eval led to. Unlike Blown accounts,
        Passed accounts don't have a Restore button — the conversion
        already created a live Funded account, so there's nothing to
        undo back into.
      </P>

      <H3>Deleting an account</H3>
      <P>
        Clicking <UI>Delete</UI> prompts you twice:
      </P>
      <UL>
        <LI>First: <em>"Delete account [name]?"</em> — confirms you actually mean to remove this account from your list. Click <UI>OK</UI> to proceed, or <UI>Cancel</UI> to back out entirely.</LI>
        <LI>If you confirmed the first, you'll see: <em>"Also delete ALL trades linked to this account? Click OK to delete trades, or Cancel to abort the account deletion."</em></LI>
      </UL>
      <P>
        On the second prompt:
      </P>
      <UL>
        <LI><UI>OK</UI> — deletes the account and every trade logged against it. Permanent. No undo.</LI>
        <LI><UI>Cancel</UI> — aborts the entire deletion. The account stays, your trades stay, nothing changes.</LI>
      </UL>

      <Callout kind="warn" title="Blown vs Delete — use Blown for breached accounts">
        Delete is permanent and removes all trade history. If you just
        blew a prop account, use <UI>Blown</UI> instead — your data
        stays intact and the account is simply hidden from active views.
        Delete is for accounts you genuinely want to erase (a duplicate,
        a test account, a sim you no longer need).
      </Callout>

      <H2>ROI Tracker tab</H2>
      <P>
        The <UI>📊 ROI Tracker</UI> tab shows your true business performance
        across all prop firms — not just trade P&L, but what you actually
        received versus what you spent in fees.
      </P>
      <P>
        At the top, four KPI cards summarize everything: Total Payouts,
        Total Expenses, Net Profit, and Overall ROI %. Below that, an
        Income by Prop Firm table breaks down each firm with columns for
        payouts, expenses, net, and ROI %. A Payout History chart shows
        the monthly breakdown as a bar chart with green bars for payouts
        and red for expenses.
      </P>
      <P>
        The ROI Tracker only appears after you've logged at least one
        payout or expense — it stays hidden until there's data to show.
        A summary version of the same data also appears on your Dashboard
        as the <UI>💰 Prop Firm ROI</UI> widget.
      </P>

      <H2>Payouts & Expenses tab</H2>
      <P>
        The <UI>💸 Payouts & Expenses</UI> tab is where you log individual
        transactions. A toggle at the top of the form switches between
        logging a Payout (money received from the firm) or an Expense
        (money paid to the firm).
      </P>
      <UL>
        <LI><strong>Payouts</strong> — select the account, enter the amount, date, and optional notes. After saving, you're prompted to reset the account balance to reflect the post-payout state.</LI>
        <LI><strong>Expenses</strong> — same fields, plus an expense type: Eval Fee, Monthly Fee, Reset Fee, or Other.</LI>
      </UL>

      <Callout kind="info" title="Balance reset after a payout">
        Most prop firms reset your account balance after a payout. When
        you log a payout, Futures OS prompts you to either reset to the
        original account size or enter a custom balance. This keeps your
        trade journal ledger aligned with your broker's actual balance.
        You can also skip the reset if your firm handles it differently.
      </Callout>

      <P>
        The transaction history shows all logged payouts and expenses
        below the form, organized by type with the account and firm
        clearly labeled on each row.
      </P>

      <H2>Trading Rules — configuring discipline</H2>
      <P>
        Each account has its own ruleset. Open Profile Settings →{" "}
        <UI>Manage Trading Rules</UI>, or use the link from a
        Portfolio account, to configure.
      </P>

      <ScreenshotFrame
        src="/guide/trading-rules.png"
        alt="Trading Rules per-account configuration"
        caption="Rules are organized into Daily Limits, Time Windows, and Event Blackouts — each with its own toggle and edit."
      />

      <P>
        At the top of the page, a <UI>Configuring rules for</UI>{" "}
        dropdown picks which account you're editing. Switch accounts
        and the rules below swap to that account's settings.
      </P>

      <H3>The three categories</H3>
      <P>
        Rules are organized into three sections, matching the kinds of
        boundaries that show up in real prop firm rulebooks:
      </P>
      <UL>
        <LI><strong>Daily Limits</strong> — caps on losses, drawdown, trade count, and loss count per day</LI>
        <LI><strong>Time Windows</strong> — when you're allowed to trade (and when you're not)</LI>
        <LI><strong>Event Blackouts</strong> — periods around scheduled news events when trading is paused</LI>
      </UL>

      <H3>Daily Limits</H3>
      <P>
        The five standard daily caps:
      </P>
      <UL>
        <LI><UI>Stop after $X loss</UI> — your daily loss limit in dollars</LI>
        <LI><UI>Stop after $X drawdown from peak</UI> — max drawdown from the day's high-water mark</LI>
        <LI><UI>Stop after $X drawdown from peak</UI> (second instance) — a secondary tighter drawdown if you want a buffer ahead of the firm's actual limit</LI>
        <LI><UI>Stop after N total trades</UI> — maximum trade count per day</LI>
        <LI><UI>Stop after N losing trades</UI> — caps the number of losers you'll take in a single day</LI>
      </UL>

      <H3>Time Windows</H3>
      <UL>
        <LI><UI>Only trade during specific hours</UI> — a "trade window" with an ET start time and a stop time. Trades outside this window get flagged as window violations.</LI>
        <LI><UI>No trading during specific times</UI> — explicit blackout windows (e.g. avoiding the open volatility from 09:30–09:45). Each gets a label so you remember why you set it.</LI>
      </UL>

      <H3>Event Blackouts</H3>
      <UL>
        <LI><UI>No trading around scheduled news</UI> — a buffer before and after high-impact events. The "Events to avoid" list (e.g. CPI, FOMC, NFP, PCE) controls which event types trigger the blackout.</LI>
      </UL>
      <P>
        The Market page handles the actual event calendar; this rule
        just tells Futures OS to flag trades that landed inside one of
        your configured event windows.
      </P>

      <H2>Apply a preset</H2>
      <P>
        At the top of the Trading Rules page, an <UI>Apply a preset</UI>{" "}
        button opens a modal of curated rulesets you can apply with one
        click. Each preset is a calibrated approach — not just a dump of
        a firm's rules, but a recommended way to trade <em>against</em>{" "}
        those rules with built-in buffers.
      </P>

      <ScreenshotFrame
        src="/guide/trading-rules-presets.png"
        alt="Apply a preset modal showing the available rulesets"
        caption="Each preset describes its philosophy and lists the specific rules it adds."
      />

      <P>
        Available presets:
      </P>
      <UL>
        <LI>
          <UI>TopStep-Safe</UI> — calibrated for a TopStep $50K combine.
          Daily loss buffer (well under TopStep's actual limit), modest
          trade cap, news blackouts on high-impact events.
        </LI>
        <LI>
          <UI>Apex-Safe</UI> — calibrated for an Apex $50K eval. Focus
          on consistency: Apex's trailing drawdown makes peak-protection
          critical, so this preset is built around that.
        </LI>
        <LI>
          <UI>Conservative</UI> — "build consistency first" approach.
          Tight trade caps, hard daily loss, drawdown protection,
          narrow trading window (the A+ hours). The strictest of the
          three.
        </LI>
      </UL>

      <Callout kind="info" title="Presets are additive, not replacing">
        Applying a preset adds its rules to your account — it does not
        remove rules you already have. If a preset's rule conflicts
        with one you've configured (e.g. you have a daily drawdown of
        $300 and the preset adds one for $400), both end up in your
        rules list and you'll need to edit or disable the one you
        don't want.
      </Callout>

      <P>
        Presets are a starting point, not a final configuration. Apply
        one that matches your account type, then edit individual rules
        to match your specific firm's rules and your own style. Always
        verify against the firm's official rulebook before trading
        live — presets are calibrated for typical firm rulesets at a
        point in time and may not reflect recent changes.
      </P>

      <H2>How rules behave</H2>
      <P>
        Two things to understand about how rules work in Futures OS:
      </P>

      <Callout kind="warn" title="Rules are advisory, not enforced">
        Trading Rules in Futures OS warn you when you're approaching
        or breaking a rule. They don't block you from logging the
        trade. Your broker and prop firm are still the actual
        enforcement layer. The rules here are about your awareness, not
        your gating.
      </Callout>

      <P>
        That advisory nature is by design. The product can't know
        whether you broke a rule because of a mistake, a deliberate
        decision, or something the rule didn't anticipate. What it
        <em>can</em> do is make the violation visible — on the
        Dashboard's Today's Plan, in Trade Entry's reflection-stage
        warning, in the Risk Discipline analytics suite. The decision
        stays with you.
      </P>

      <H3>The Edit, toggle, and delete controls</H3>
      <P>
        Every rule has four controls:
      </P>
      <UL>
        <LI>Green toggle on the left — turn the rule on or off for this account</LI>
        <LI><UI>WARN</UI> badge — confirms the rule is active and will surface warnings when crossed</LI>
        <LI><UI>Edit</UI> button — open the rule's settings (the limit, the time window, etc.)</LI>
        <LI>🗑️ trash icon on the right — remove the rule from this account's ruleset entirely</LI>
      </UL>
      <P>
        Toggle a rule off and it stops appearing in warnings but stays
        in your list — useful when you want to silence a rule
        temporarily (sim session, a deliberate "off day"). Click the
        trash icon and the rule is removed from the account's ruleset
        entirely; it'll no longer show up on the Dashboard's Today's
        Plan or anywhere else. Deleting a rule doesn't touch any
        trade data — only the rule definition is removed.
      </P>

      <H2>How rules show up elsewhere</H2>
      <P>
        Once configured, your rules feed into several other surfaces:
      </P>
      <UL>
        <LI><strong>Dashboard's Today's Plan</strong> — every active rule shows up as a status row with a green check or red X, refreshed in real time as you log trades</LI>
        <LI><strong>Trade Entry's Reflection stage</strong> — if a trade broke any rules, a yellow warning banner lists them when you reach the Reflection stage</LI>
        <LI><strong>Risk Discipline analytics</strong> — the Stop Adherence, Stop Widening, and Daily Drawdown Proximity detectors use your configured rules as the reference point</LI>
        <LI><strong>Eval Survival</strong> — separate from your personal rules, but uses similar concepts to test your history against each prop firm's published ruleset</LI>
      </UL>

      <Callout kind="tip">
        For combine traders, the highest-leverage rule to set carefully
        is your daily drawdown — match it to your prop firm's rule
        exactly, or set it slightly tighter as a buffer. Today's Plan
        on the Dashboard then becomes a real-time read on how close
        you are to the limit your account actually lives or dies by.
      </Callout>

      <H2>Per-account customization</H2>
      <P>
        Rules are saved per-account. A reasonable setup might be:
      </P>
      <UL>
        <LI><strong>Your combine</strong> — strict rules matching the firm's published limits. Time windows narrow to your best trading hours. News blackouts on.</LI>
        <LI><strong>Your funded account</strong> — same strict rules as the combine, plus maybe a tighter buffer on drawdown.</LI>
        <LI><strong>A sim account</strong> — rules off or relaxed, since you're using it to experiment or learn.</LI>
      </UL>
      <P>
        Switch between accounts in the rules dropdown to manage each
        one separately. Changes to one account's rules don't affect
        any other account.
      </P>
    </GuideLayout>
  );
}
